import { useState } from 'react';
import { TICKET_ATTACHMENT_MIME_TYPES, MAX_TICKET_ATTACHMENTS } from '../../lib/validators/ticketSchema';

const MAX_BYTES = 5 * 1024 * 1024;

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Shared attachment picker for ticket creation and replies — up to 5 files,
 * jpg/jpeg/png/pdf, 5MB each. Same pattern as KycFilesInput (client-side
 * pre-check on selection for instant feedback, server is still the real
 * source of truth), just a higher file cap per the ticket spec.
 */
export default function TicketAttachmentsInput({ value = [], onChange, error, label = 'Attachments (optional, up to 5 files)' }) {
  const [localError, setLocalError] = useState(null);

  const addFiles = (fileList) => {
    setLocalError(null);
    const incoming = Array.from(fileList ?? []);
    const accepted = [];

    for (const file of incoming) {
      if (value.length + accepted.length >= MAX_TICKET_ATTACHMENTS) {
        setLocalError(`Only ${MAX_TICKET_ATTACHMENTS} files allowed — remove one to add another.`);
        break;
      }
      if (!TICKET_ATTACHMENT_MIME_TYPES.includes(file.type)) {
        setLocalError(`${file.name} isn't a JPG, PNG, or PDF.`);
        continue;
      }
      if (file.size > MAX_BYTES) {
        setLocalError(`${file.name} is larger than 5MB.`);
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length) onChange([...value, ...accepted]);
  };

  const removeAt = (idx) => {
    setLocalError(null);
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground-muted mb-1.5">{label}</label>

      {value.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {value.map((file, idx) => (
            <li
              key={`${file.name}-${idx}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background-elevated px-3 py-2 text-xs"
            >
              <span className="truncate text-foreground">
                {file.name} <span className="text-foreground-subtle">({formatBytes(file.size)})</span>
              </span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="shrink-0 text-foreground-subtle hover:text-danger transition-colors"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {value.length < MAX_TICKET_ATTACHMENTS && (
        <input
          type="file"
          accept="image/jpeg,image/png,image/jpg,application/pdf"
          multiple
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
          className="w-full text-sm text-foreground-muted file:mr-3 file:rounded-lg file:border-0 file:bg-background-elevated file:px-3 file:py-2 file:text-sm file:font-medium file:text-foreground file:cursor-pointer hover:file:bg-background-surface"
        />
      )}

      <p className="mt-1.5 text-xs text-foreground-subtle">JPG, PNG, or PDF. Up to 5MB each, max {MAX_TICKET_ATTACHMENTS} files.</p>
      {(localError || error) && <p className="mt-1.5 text-xs text-danger">{localError || error}</p>}
    </div>
  );
}
